import * as React from 'react';

import useUrlState from '@ahooksjs/use-url-state';
import { useGetState, useMemoizedFn, useRequest } from 'ahooks';
import { Helmet } from 'react-helmet-async';

import { searchProductsComments } from 'services';
import type { ProductCommentStatus } from 'types';
import { PATHNAME } from 'utils';

import { ProductsCommentsHeader, ProductsCommentsTable } from './components';

const {
  QUERY_PARAM: {
    DEFAULT: {
      SORT_TYPE: DEFAULT_SORT_TYPE,
      PAGE_NUMBER: DEFAULT_PAGE_NUMBER,
      PAGE_SIZE: DEFAULT_PAGE_SIZE,
    },
    SORT_TYPE: { BASE_URL: SORT_TYPE_BASE_URL },
    SEARCH_KEY: { BASE_URL: SEARCH_KEY_BASE_URL },
    PAGE_NUMBER: { BASE_URL: PAGE_NUMBER_BASE_URL },
  },
} = PATHNAME;

export default function ProductsComments() {
  const [
    {
      [SORT_TYPE_BASE_URL]: sortType,
      [SEARCH_KEY_BASE_URL]: searchKey,
      [PAGE_NUMBER_BASE_URL]: pageNumber,
    },
    setUrlState,
  ] = useUrlState(
    {
      [SORT_TYPE_BASE_URL]: DEFAULT_SORT_TYPE,
      [SEARCH_KEY_BASE_URL]: '',
      [PAGE_NUMBER_BASE_URL]: DEFAULT_PAGE_NUMBER,
    },
    { navigateMode: 'replace' },
  );
  const [pageSize, setPageSize] = useGetState<number>(DEFAULT_PAGE_SIZE);
  const [statuses, setStatuses] = useGetState<ProductCommentStatus[]>();
  const [lowerBoundCreateDate, setLowerBoundCreateDate] = useGetState<string>();
  const [upperBoundCreateDate, setUpperBoundCreateDate] = useGetState<string>();
  const { data, loading, refresh } = useRequest(
    async () =>
      await searchProductsComments({
        sortType,
        searchKey,
        pageNumber,
        pageSize,
        statuses,
        lowerBoundCreateDate,
        upperBoundCreateDate,
      }),
    {
      refreshDeps: [
        sortType,
        searchKey,
        pageNumber,
        pageSize,
        statuses,
        lowerBoundCreateDate,
        upperBoundCreateDate,
      ],
    },
  );

  const { total = 0, data: productsComments = [] } = data ?? {};

  const handleSearchKey = useMemoizedFn((updatedSearchKey: string) => {
    setUrlState({ [SEARCH_KEY_BASE_URL]: updatedSearchKey });
  });
  const handlePageNumber = useMemoizedFn((updatedPageNumber: number) => {
    setUrlState({ [PAGE_NUMBER_BASE_URL]: updatedPageNumber });
  });
  const handlePageSize = useMemoizedFn((updatedPageSize: number) => {
    setPageSize(updatedPageSize);
  });
  const handleStatuses = useMemoizedFn(
    (updatedStatuses: ProductCommentStatus[]) => {
      setStatuses(updatedStatuses);
    },
  );
  const handleLowerBoundCreateDate = useMemoizedFn(
    (updatedLowerBoundCreateDate?: string) => {
      setLowerBoundCreateDate(updatedLowerBoundCreateDate);
    },
  );
  const handleUpperBoundCreateDate = useMemoizedFn(
    (updatedUpperBoundCreateDate?: string) => {
      setUpperBoundCreateDate(updatedUpperBoundCreateDate);
    },
  );

  return (
    <React.Fragment>
      <Helmet>
        <title>دیدگاه کاربرها | کاشی لند</title>
      </Helmet>
      <div className='rounded bg-white p-6'>
        <ProductsCommentsHeader
          searchKey={searchKey}
          handleSearchKey={handleSearchKey}
          handleLowerBoundCreateDate={handleLowerBoundCreateDate}
          handleUpperBoundCreateDate={handleUpperBoundCreateDate}
        />
        <ProductsCommentsTable
          loading={loading}
          total={total}
          productsComments={productsComments}
          pageSize={pageSize}
          pageNumber={pageNumber}
          handleStatuses={handleStatuses}
          handlePageSize={handlePageSize}
          handlePageNumber={handlePageNumber}
          handleRefreshData={refresh}
        />
      </div>
    </React.Fragment>
  );
}
